import {
  Grid,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Button,
  FormHelperText,
} from "@mui/material";
import React from "react";
import { cloneDeep } from "lodash";
import states from "components/pages/transmissions/modal/patient/static/states";
import usePatientInformation from "components/pages/transmissions/modal/patient/tabs/generalInformation/patientInformation/usePatientInformation";
import SubmitButton from "components/Button/SubmitButton";
import PhoneMaskInput from "components/common/input/PhoneMaskInput";
import { Formik } from "formik";
import * as Yup from "yup";
import { PHONE_NUMBER_REGEX } from "static/constants";
import { truncateInput } from "utils/string";

const validationSchema = Yup.object({
  phone1: Yup.string().matches(
    PHONE_NUMBER_REGEX,
    "Invalid phone number format"
  ),
  phone2: Yup.string().matches(
    PHONE_NUMBER_REGEX,
    "Invalid phone number format"
  ),
  email: Yup.string().email("Invalid email"),
  street1: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zip: Yup.string(),
  emergency_contact: Yup.object().shape({
    phone: Yup.string().matches(
      PHONE_NUMBER_REGEX,
      "Invalid phone number format"
    ),
    name: Yup.string().max(
      50,
      "Emergency Contact Name must be 50 characters or less"
    ),
    relation: Yup.string().max(
      50,
      "Emergency Contact Relationship must be 50 characters or less"
    ),
  }),
  notes: Yup.string(),
});

const PatientInformationForm = (props) => {
  const { isLoading, handleCancel, setIsEditMode, handleSubmit } = props;
  const { newPatientInformation, handleInputChange } = usePatientInformation();

  return (
    <Formik
      initialValues={{
        phone1: newPatientInformation?.phone1 || "",
        phone2: newPatientInformation?.phone2 || "",
        street1: newPatientInformation?.street1 || "",
        street2: newPatientInformation?.street2 || "",
        email: newPatientInformation?.email || "",
        city: newPatientInformation?.city || "",
        state: newPatientInformation?.state || "",
        zip: newPatientInformation?.zip || "",
        emergency_contact: {
          phone: newPatientInformation?.emergency_contact?.phone || "",
          name: newPatientInformation?.emergency_contact?.name || "",
          relation: newPatientInformation?.emergency_contact?.relation || "",
        },
        notes: newPatientInformation?.notes || "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ touched, errors, handleSubmit, resetForm, setFieldValue }) => (
        <>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Phone #1"
                name="phone1"
                data-text="contact-information-phone1-field"
                value={newPatientInformation?.phone1}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.phone1 = value;

                  handleInputChange(copy);
                  setFieldValue("phone1", value);
                }}
                error={touched.phone1 && Boolean(errors.phone1)}
                helperText={touched.phone1 && errors.phone1}
                InputProps={{ inputComponent: PhoneMaskInput }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Phone #2"
                name="phone2"
                data-text="contact-information-phone2-field"
                value={newPatientInformation?.phone2}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.phone2 = value;

                  handleInputChange(copy);
                  setFieldValue("phone2", value);
                }}
                error={touched.phone2 && Boolean(errors.phone2)}
                helperText={touched.phone2 && errors.phone2}
                InputProps={{ inputComponent: PhoneMaskInput }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Email"
                name="email"
                data-text="contact-information-email-field"
                value={newPatientInformation?.email}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.email = value;

                  handleInputChange(copy);
                  setFieldValue("email", value);
                }}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Emergency Contact Phone"
                name="emergency_contact"
                data-text="contact-information-emergency-contact-phone-field"
                value={newPatientInformation?.emergency_contact?.phone || ""}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.emergency_contact = {
                    ...copy.emergency_contact,
                    phone: value,
                  };

                  handleInputChange(copy);
                  setFieldValue("emergency_contact", copy.emergency_contact);
                }}
                error={
                  touched.emergency_contact?.phone &&
                  Boolean(errors.emergency_contact?.phone)
                }
                helperText={
                  touched.emergency_contact?.phone &&
                  errors.emergency_contact?.phone
                }
                InputProps={{ inputComponent: PhoneMaskInput }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Emergency Contact Name"
                name="emergency_contact_name"
                data-text="contact-information-emergency-contact-name-field"
                value={newPatientInformation?.emergency_contact?.name || ""}
                onInput={(event) => truncateInput(event, 50)}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.emergency_contact["name"] = value;

                  handleInputChange(copy);
                  setFieldValue("emergency_contact", copy.emergency_contact);
                }}
                error={
                  touched.emergency_contact?.name &&
                  Boolean(errors.emergency_contact?.name)
                }
                helperText={
                  touched.emergency_contact?.name &&
                  errors.emergency_contact?.name
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Emergency Contact Relationship"
                name="emergency_contact_relation"
                data-text="contact-information-emergency-contact-relationship-field"
                value={newPatientInformation?.emergency_contact?.relation || ""}
                onInput={(event) => truncateInput(event, 50)}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.emergency_contact["relation"] = value;

                  handleInputChange(copy);
                  setFieldValue("emergency_contact", copy.emergency_contact);
                }}
                error={
                  touched.emergency_contact?.relation &&
                  Boolean(errors.emergency_contact?.relation)
                }
                helperText={
                  touched.emergency_contact?.relation &&
                  errors.emergency_contact?.relation
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Street Address #1"
                name="street1"
                data-text="contact-information-street-address-1-field"
                value={newPatientInformation?.street1}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.street1 = value;

                  handleInputChange(copy);
                  setFieldValue("street1", value);
                }}
                error={touched.street1 && Boolean(errors.street1)}
                helperText={touched.street1 && errors.street1}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Street Address #2"
                name="street2"
                data-text="contact-information-street-address-2-field"
                value={newPatientInformation?.street2}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.street2 = value;

                  handleInputChange(copy);
                  setFieldValue("street2", value);
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="City"
                name="city"
                data-text="contact-information-city-field"
                value={newPatientInformation?.city}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.city = value;

                  handleInputChange(copy);
                  setFieldValue("city", value);
                }}
                error={touched.city && Boolean(errors.city)}
                helperText={touched.city && errors.city}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel
                  id="state-label"
                  error={touched.state && Boolean(errors.state)}
                >
                  State
                </InputLabel>
                <Select
                  labelId="state-label"
                  id="state"
                  name="state"
                  label="State"
                  data-text="contact-information-state-field"
                  value={newPatientInformation?.state}
                  onChange={(event) => {
                    const value = event.target.value;

                    const copy = cloneDeep(newPatientInformation);
                    copy.state = value;

                    handleInputChange(copy);
                    setFieldValue("state", value);
                  }}
                  error={touched.state && Boolean(errors.state)}
                >
                  {states.map((state, index) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {touched.state && Boolean(errors.state) && (
                <FormHelperText error>{errors.state}</FormHelperText>
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                label="Zip"
                name="zip"
                data-text="contact-information-zip-field"
                value={newPatientInformation?.zip}
                onChange={(event) => {
                  const value = event.target.value;

                  const copy = cloneDeep(newPatientInformation);
                  copy.zip = value;

                  handleInputChange(copy);
                  setFieldValue("zip", value);
                }}
                error={touched.zip && Boolean(errors.zip)}
                helperText={touched.zip && errors.zip}
              />
            </Grid>
          </Grid>
          <div
            style={{ textAlign: "right", gap: "10px" }}
            className="d-flex flex-end mt-2 mb-3"
          >
            <Button
              variant="outlined"
              color="secondary"
              data-action="contact-information-cancel-button"
              onClick={() => {
                handleCancel();
                setIsEditMode(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <SubmitButton
              handleClick={handleSubmit}
              buttonText={isLoading ? "Saving..." : "Save"}
              data-action="contact-information-save-button"
            />
          </div>
        </>
      )}
    </Formik>
  );
};

export default PatientInformationForm;
